/**
 * screens/Settings/components/RulesCard
 * The two org defaults that shape what the console flags:
 *   "Count is overdue after"    3 / 7 / 14 / 30 days
 *   "Receipt required on spends" Required / Optional
 *
 * Both are chip rows rather than dropdowns, for the same reason every other
 * choice in this product is: one click, no picker sheet, and the options are
 * visible without opening anything.
 */
import { StyleSheet, Text, View } from 'react-native';
import { Chip } from '../../../components';
import { neutral } from '../../../theme/colors';
import { sans, size as fontSize, leading } from '../../../theme/typography';
import { STALE_DAYS, type Rules, type StaleDays } from '../../../schemas/config';
import SettingsCard from './SettingsCard';
import SettingsRow from './SettingsRow';

export interface RulesCardProps {
  rules: Rules | null;
  onSetStaleDays: (days: StaleDays) => void;
  onSetReceiptRequired: (required: boolean) => void;
}

export function RulesCard({ rules, onSetStaleDays, onSetReceiptRequired }: RulesCardProps) {
  return (
    <SettingsCard title="Rules" subtitle="Two defaults that shape what the console flags.">
      <SettingsRow roomy>
        <Text style={styles.label}>Count is overdue after</Text>
        <Text style={styles.hint}>
          Drives the unreconciled count on Overview and the age chip in the register.
        </Text>
        <View style={styles.chips}>
          {STALE_DAYS.map((days) => (
            <Chip
              key={days}
              label={`${days} days`}
              selected={rules?.stale_days === days}
              onPress={() => onSetStaleDays(days)}
            />
          ))}
        </View>
      </SettingsRow>

      <SettingsRow roomy divided={false}>
        <Text style={styles.label}>Receipt required on spends</Text>
        <Text style={styles.hint}>Whether the spend form asks for evidence by default.</Text>
        <View style={styles.chips}>
          <Chip
            label="Required"
            selected={rules?.receipt_required === true}
            onPress={() => onSetReceiptRequired(true)}
          />
          <Chip
            label="Optional"
            selected={rules?.receipt_required === false}
            onPress={() => onSetReceiptRequired(false)}
          />
        </View>
      </SettingsRow>
    </SettingsCard>
  );
}

const styles = StyleSheet.create({
  label: { ...sans('500'), fontSize: fontSize.button, color: neutral.ink, marginBottom: 2 },
  hint: {
    ...sans('400'),
    fontSize: fontSize.meta,
    lineHeight: leading(fontSize.meta),
    color: neutral.grey600,
    marginBottom: 9,
  },
  chips: { flexDirection: 'row', flexWrap: 'wrap', gap: 5 },
});

export default RulesCard;
